import clsx from "clsx"
import { FC, memo, useEffect, useState } from "react"
import { SpinnerCircular } from "spinners-react"
import { IImageLoader } from "."
import styles from "./ImageLoader.module.scss"

/**
 * Module-level cache of URLs that have successfully loaded at least once.
 * Shared across all ImageLoader instances so remounts with the same src
 * start in "loaded" status — no flash of spinner. Write-only on load;
 * entries are never removed (a loaded image does not "become unloaded").
 */
const loadedSrcs = new Set<string>()

/**
 * Renders an <img> with a spinner overlay while the image is loading and
 * falls back to `defaultImg` on error or when `src` is falsy.
 *
 * Implementation notes:
 * - The <img> is always in the DOM. During loading it gets `display:none`
 *   so the spinner takes its visual slot; browsers still fetch images
 *   under `display:none`, so onLoad fires normally.
 * - React reconciliation keeps the same <img> across loading→loaded —
 *   no remount, no re-decode, no second fetch.
 * - Unmount / src change: the browser cancels the in-flight fetch on its
 *   own, no manual cleanup required.
 *
 * Known limitation (intentional):
 * - Loading state does not reserve image size — layout shifts when the
 *   <img> transitions from `display:none` to visible. Consumers that
 *   need a fixed slot should size the container themselves.
 */
const ImageLoaderComp: FC<IImageLoader> = ({
  spinner,
  defaultImg,
  onLoad,
  onError,
  ...props
}) => {
  const { src } = props
  const [status, setStatus] = useState<"loading" | "loaded" | "error">(() =>
    src && loadedSrcs.has(src) ? "loaded" : "loading"
  )

  useEffect(() => {
    if (!src) return
    setStatus(loadedSrcs.has(src) ? "loaded" : "loading")
  }, [src])

  if (!src || status === "error") {
    return defaultImg ? <img alt="" {...defaultImg} /> : null
  }

  const isLoading = status === "loading"

  return (
    <>
      <img
        alt=""
        {...props}
        style={isLoading ? { ...props.style, display: "none" } : props.style}
        onLoad={(e) => {
          loadedSrcs.add(src)
          setStatus("loaded")
          onLoad?.(e)
        }}
        onError={(e) => {
          setStatus("error")
          onError?.(e)
        }}
      />
      {isLoading && (
        <SpinnerCircular
          {...spinner}
          className={clsx(styles.ImageLoader_spinner, spinner?.className)}
          size={spinner?.size ?? 32}
          color={spinner?.color || "#ffd180"}
        />
      )}
    </>
  )
}

export const ImageLoader = memo(ImageLoaderComp)
